/**
 * #1392 — focus the app header search box from anywhere.
 *
 * The search input lives in the design-system Header (the
 * ``CollapsibleSearch`` molecule on desktop, a plain mobile input on
 * small screens). Rather than thread a ref through the design-system
 * (which other agents/streams also touch), the app-search controller
 * focuses it via the already-rendered DOM landmarks. Returns true if a
 * search affordance was found and acted upon.
 */

/**
 * An element is "visible enough" to focus if it participates in layout.
 * ``offsetParent === null`` catches ``display:none`` ancestors (e.g. the
 * collapsed mobile menu that still renders ``.header-mobile-search-input``
 * on desktop); ``getClientRects().length`` catches the rest. Guards the
 * desktop shortcut from landing on the hidden mobile input.
 */
function isVisible(el: HTMLElement): boolean {
  return el.offsetParent !== null || el.getClientRects().length > 0;
}

function focusVisibleInput(selector: string): boolean {
  const inputs = Array.from(
    document.querySelectorAll<HTMLInputElement>(selector),
  );
  const input = inputs.find(isVisible);
  if (!input) return false;
  input.focus();
  input.select?.();
  return true;
}

export function focusAppSearchInput(): boolean {
  // Desktop: collapsible search already expanded → focus the input.
  if (focusVisibleInput('.collapsible-search__input')) return true;

  // Desktop collapsed: click the visible trigger to expand. The
  // CollapsibleSearch molecule focuses its own input ~100ms after
  // expanding, so the click alone puts the cursor in the box. Checked
  // BEFORE the mobile input so the visible desktop affordance always
  // wins on a wide viewport.
  const triggers = Array.from(
    document.querySelectorAll<HTMLButtonElement>('.collapsible-search__trigger'),
  );
  const trigger = triggers.find(isVisible);
  if (trigger) {
    trigger.click();
    return true;
  }

  // Mobile: a plain search input rendered directly in the header.
  if (focusVisibleInput('.header-mobile-search-input')) return true;

  return false;
}
